// A labelled number the reader types, for the calculator's inputs.
//
// The text is theirs while they type and follows the value again once they
// leave, as on the Margin page. An empty box hands back undefined, which the
// page reads as "the default": the placeholder says what that default is, so a
// cleared box never looks like a zero.

import { useEffect, useId, useState, type ReactNode } from "react";
import styles from "./PricingPage.module.css";

export interface NumberFieldProps {
  label: string;
  /** Undefined when the default is in force; the placeholder then shows it. */
  value: number | undefined;
  placeholder?: string;
  prefix?: string;
  suffix?: string;
  /** Places the value is shown to once the reader leaves the box. */
  decimals?: number;
  min?: number;
  max?: number;
  /** A line under the box: where the default came from, or what it means. */
  hint?: ReactNode;
  onChange: (value: number | undefined) => void;
}

export function NumberField({
  label,
  value,
  placeholder,
  prefix,
  suffix,
  decimals = 2,
  min = 0,
  max,
  hint,
  onChange,
}: NumberFieldProps): ReactNode {
  const id = useId();
  const shown = value === undefined ? "" : trimNumber(value, decimals);
  const [text, setText] = useState(shown);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(shown);
  }, [shown, editing]);

  return (
    <div className={styles.field}>
      <label htmlFor={id} className={styles.fieldLabel}>
        {label}
      </label>
      <span className={styles.fieldBox}>
        {prefix !== undefined && <span className={styles.affix}>{prefix}</span>}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          min={min}
          {...(max === undefined ? {} : { max })}
          step="any"
          value={text}
          placeholder={placeholder}
          onFocus={() => setEditing(true)}
          onBlur={() => setEditing(false)}
          onChange={(event) => {
            setText(event.target.value);
            const raw = event.target.value.trim();
            if (raw === "") return onChange(undefined);
            const parsed = Number(raw);
            if (!Number.isFinite(parsed) || parsed < min) return;
            onChange(max === undefined ? parsed : Math.min(parsed, max));
          }}
        />
        {suffix !== undefined && <span className={styles.affix}>{suffix}</span>}
      </span>
      {hint !== undefined && <span className={styles.fieldHint}>{hint}</span>}
    </div>
  );
}

export function trimNumber(value: number, decimals: number): string {
  const fixed = value.toFixed(decimals);
  return fixed.includes(".") ? fixed.replace(/0+$/, "").replace(/\.$/, "") : fixed;
}
